"use client";

import { usePathname } from "next/navigation";
import { useEffect, useLayoutEffect, useState } from "react";
import { HeaderHeightPublisher } from "./HeaderHeightPublisher";

// useLayoutEffect on the client, useEffect on the server — avoids the SSR
// warning while still letting us flip the header to overlay mode before the
// browser's first paint (so there's no flash / layout jump on hero pages).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Client wrapper for a structured header that opts into `transparentHeader`.
 *
 * On pages whose leading visual is a full-bleed hero (`[data-sf-hero]` near the
 * top of the page), the header renders as a fixed, transparent overlay — with a
 * top-down scrim + white glyphs — and animates to a solid background once the
 * user scrolls past the hero's top edge. On every other page it falls back to
 * the normal solid, in-flow sticky header, so content is never hidden behind a
 * fixed overlay. Detection re-runs on client navigation (pathname change).
 */
export function StorefrontHeaderShell({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [overlay, setOverlay] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const hero = document.querySelector(
      "[data-storefront] main [data-sf-hero]",
    );
    // Treat it as a leading hero only when it starts within the first viewport.
    const isOverlay =
      !!hero &&
      hero.getBoundingClientRect().top + window.scrollY <
        window.innerHeight * 0.9;
    setOverlay(isOverlay);
    setScrolled(isOverlay ? window.scrollY > 24 : false);
  }, [pathname]);

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  if (!overlay) {
    // No leading hero on this page → normal solid, in-flow sticky header.
    return (
      <header className="sticky top-0 z-30 bg-background shadow-sm">
        <HeaderHeightPublisher />
        {children}
      </header>
    );
  }

  return (
    <header
      className={`sf-header ${scrolled ? "sf-header--solid" : "sf-header--transparent"}`}
    >
      {/* Remounts when the branch flips, so the new header is measured. */}
      <HeaderHeightPublisher />
      {children}
    </header>
  );
}
